/*
  Hikayat Omi silk gradient.

  A slow, domain-warped colour wash drawn with WebGL, used as a section
  background. Each <canvas data-silk> is set up independently:

    data-colors     four hex colours, darkest to brightest, comma separated
    data-speed      flow speed multiplier (default 1)

  The section that contains the canvas gets the class "has-silk" once the
  first frame is drawn, so CSS can retire whatever background it replaces.
  When WebGL is missing, the context is lost, or the device is too slow,
  the class is removed again and the original background shows through.

  Behaviour that keeps it light:
  - renders at reduced resolution (the image is soft, so this is invisible)
  - animates only while the canvas is on screen and the tab is visible
  - draws a single still frame for prefers-reduced-motion and for devices
    with two or fewer CPU cores
  - if the first second of frames averages under ~28fps, it settles on a
    still frame instead of animating
*/
(function () {
  'use strict';

  var VERT = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';

  var FRAG = [
    'precision mediump float;',
    'uniform vec2 uRes;uniform float uTime;uniform vec2 uPtr;uniform float uPull;',
    'uniform vec3 uC0;uniform vec3 uC1;uniform vec3 uC2;uniform vec3 uC3;',
    'vec2 h(vec2 p){p=vec2(dot(p,vec2(127.1,311.7)),dot(p,vec2(269.5,183.3)));return -1.+2.*fract(sin(p)*43758.5453);}',
    'float n(vec2 p){vec2 i=floor(p),f=fract(p),u=f*f*(3.-2.*f);',
    ' return mix(mix(dot(h(i),f),dot(h(i+vec2(1.,0.)),f-vec2(1.,0.)),u.x),mix(dot(h(i+vec2(0.,1.)),f-vec2(0.,1.)),dot(h(i+vec2(1.,1.)),f-vec2(1.,1.)),u.x),u.y);}',
    'float fbm(vec2 p){float v=0.,a=.5;mat2 m=mat2(1.6,1.2,-1.2,1.6);for(int i=0;i<4;i++){v+=a*n(p);p=m*p;a*=.5;}return v;}',
    'void main(){',
    ' float asp=uRes.x/uRes.y;vec2 p=gl_FragCoord.xy/uRes;p.x*=asp;',
    ' float t=uTime*.045;',
    // a gentle swirl around the pointer
    ' vec2 m=vec2(uPtr.x*asp,uPtr.y);vec2 d=p-m;float s=uPull*exp(-dot(d,d)*5.);',
    ' float a=s*1.1;p=m+mat2(cos(a),-sin(a),sin(a),cos(a))*d;',
    // two rounds of domain warping give the folded, silky look
    ' vec2 q=vec2(fbm(p*1.15+vec2(0.,t)),fbm(p*1.15+vec2(5.2,1.3)-t));',
    ' vec2 r=vec2(fbm(p*1.3+2.*q+vec2(1.7,9.2)+t*.7),fbm(p*1.3+2.*q+vec2(8.3,2.8)-t*.6));',
    ' float f=fbm(p*1.05+2.3*r);',
    ' float k=clamp(f*.95+.5,0.,1.);',
    ' vec3 c=mix(uC0,uC1,smoothstep(.12,.55,k));',
    ' c=mix(c,uC2,smoothstep(.42,.86,length(q)*.85+.25*k));',
    ' c=mix(c,uC3,smoothstep(.6,.96,r.x*.5+.5)*.6);',
    // a faint travelling sheen, like light on fabric
    ' c+=pow(max(0.,sin((p.x+p.y*.6+f*2.2)*3.+t*5.)),10.)*.06;',
    ' gl_FragColor=vec4(c,1.);',
    '}'
  ].join('\n');

  function hex(c) {
    c = c.trim().replace('#', '');
    return [parseInt(c.slice(0, 2), 16) / 255, parseInt(c.slice(2, 4), 16) / 255, parseInt(c.slice(4, 6), 16) / 255];
  }

  function shader(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
  }

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var weak = (navigator.hardwareConcurrency || 4) <= 2;

  function Silk(canvas) {
    var host = canvas.parentNode;
    var gl = canvas.getContext('webgl', { antialias: false, alpha: false, depth: false, stencil: false, powerPreference: 'low-power', preserveDrawingBuffer: false });
    if (!gl) return;

    var vs = shader(gl, gl.VERTEX_SHADER, VERT), fs = shader(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    var prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'a');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var U = {};
    ['uRes', 'uTime', 'uPtr', 'uPull', 'uC0', 'uC1', 'uC2', 'uC3'].forEach(function (k) { U[k] = gl.getUniformLocation(prog, k); });

    var colors = (canvas.getAttribute('data-colors') || '#3f1f25,#5c2f35,#8a4351,#c97f88').split(',');
    for (var i = 0; i < 4; i++) gl.uniform3fv(U['uC' + i], hex(colors[Math.min(i, colors.length - 1)]));
    var speed = parseFloat(canvas.getAttribute('data-speed') || '1');

    // render well below device resolution: the wash is soft, and it keeps the cost tiny
    var scale = Math.min(window.devicePixelRatio || 1, 1.5) * 0.5;
    function resize() {
      var w = Math.max(2, Math.round(canvas.clientWidth * scale)), h = Math.max(2, Math.round(canvas.clientHeight * scale));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; gl.viewport(0, 0, w, h); }
      gl.uniform2f(U.uRes, w, h);
    }

    var ptr = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5, pull: 0, tpull: 0, last: 0 };
    host.addEventListener('pointermove', function (e) {
      var r = canvas.getBoundingClientRect();
      ptr.tx = (e.clientX - r.left) / r.width;
      ptr.ty = 1 - (e.clientY - r.top) / r.height;
      ptr.tpull = 1; ptr.last = performance.now();
    }, { passive: true });

    var start = performance.now() - Math.random() * 20000; // each canvas starts at a different moment of the flow
    var running = false, visible = false, still = reduce || weak, frames = 0, probeStart = 0, raf = 0;

    function draw(now) {
      if (now - ptr.last > 1800) ptr.tpull = 0;
      ptr.x += (ptr.tx - ptr.x) * 0.06; ptr.y += (ptr.ty - ptr.y) * 0.06; ptr.pull += (ptr.tpull - ptr.pull) * 0.04;
      gl.uniform1f(U.uTime, ((now - start) / 1000) * speed);
      gl.uniform2f(U.uPtr, ptr.x, ptr.y);
      gl.uniform1f(U.uPull, ptr.pull);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    function loop(now) {
      if (!running) return;
      draw(now);
      // performance probe: if the first second runs slowly, settle on a still frame
      frames++;
      if (frames === 1) probeStart = now;
      else if (frames === 30 && (now - probeStart) / 29 > 36) { still = true; running = false; return; }
      raf = requestAnimationFrame(loop);
    }

    function update() {
      var should = visible && !still && !document.hidden;
      if (should && !running) { running = true; frames = 0; raf = requestAnimationFrame(loop); }
      else if (!should && running) { running = false; cancelAnimationFrame(raf); }
    }

    function first() {
      resize();
      draw(performance.now());
      host.classList.add('has-silk');
      canvas.classList.add('is-on');
    }

    canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault(); running = false; still = true;
      host.classList.remove('has-silk'); canvas.classList.remove('is-on');
    });

    if ('ResizeObserver' in window) new ResizeObserver(function () { resize(); if (!running) draw(performance.now()); }).observe(canvas);
    else window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', update);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          visible = e.isIntersecting;
          if (visible && !canvas.classList.contains('is-on')) first();
          update();
        });
      }, { rootMargin: '200px 0px' }).observe(canvas);
    } else { first(); visible = true; update(); }
  }

  function init() { [].forEach.call(document.querySelectorAll('canvas[data-silk]'), function (c) { try { Silk(c); } catch (e) {} }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
